import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Clock, Save, Users, Lock } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * GroomerSchedules — admin page for editing employees' working hours.
 *
 * Roadmap item #17 Phase 2 added a self-mode: when mounted with
 * `selfMode` (or rendered for a groomer-role user), the component
 * locks down to that user's own groomer row, hides the picker, and
 * gates Save behind the `employees_can_set_own_hours` system setting.
 *
 * Admin mode also gets a header toggle for the setting so it can be
 * flipped without leaving the schedule editor.
 */
const GroomerSchedules = ({ selfMode: selfModeProp = false } = {}) => {
  const { isAdmin, isGroomer } = useAuth();
  // selfMode is forced for groomer-role users; admins can also opt in
  // via prop (currently unused, but reserved for a future "edit my
  // own" admin shortcut).
  const selfMode = selfModeProp || (!isAdmin && isGroomer);
  const [groomers, setGroomers] = useState([]);
  const [selectedGroomer, setSelectedGroomer] = useState(null);
  const [groomerSchedule, setGroomerSchedule] = useState({});
  const [businessHours, setBusinessHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Roadmap item #17 Phase 2: track + toggle the system setting that
  // controls whether employees can edit their own schedule.
  const [allowSelfEdit, setAllowSelfEdit] = useState(false);
  const [savingSetting, setSavingSetting] = useState(false);

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  useEffect(() => {
    loadBusinessHours();
    loadAllowSelfEditSetting();
    if (selfMode) {
      loadOwnGroomer();
    } else {
      loadGroomers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedGroomer) {
      loadGroomerSchedule(selectedGroomer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroomer]);

  const loadGroomers = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/groomers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGroomers(response.data);
    } catch (error) {
      console.error('Load groomers error:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  // Self-mode entry point: pull just the current user's groomer row
  // via /api/groomers/me, pre-select it, and render the editor as if
  // the admin had picked them from the list.
  const loadOwnGroomer = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/groomers/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const me = response.data;
      setGroomers([me]);
      setSelectedGroomer(me.id);
    } catch (error) {
      console.error('Load own groomer error:', error);
      toast.error('Could not load your employee profile');
    } finally {
      setLoading(false);
    }
  };

  // Reads the `employees_can_set_own_hours` system setting. Used both
  // to render the admin toggle (admin mode) and to gate Save +
  // editability (self mode). Falls back to false on any error so we
  // never accidentally widen permissions.
  const loadAllowSelfEditSetting = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const res = await axios.get(`${API_URL}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const row = (res.data || []).find((r) => r.setting_key === 'employees_can_set_own_hours');
      const v = String(row?.setting_value ?? 'false').toLowerCase();
      setAllowSelfEdit(['1', 'true', 'yes', 'on'].includes(v));
    } catch (e) {
      setAllowSelfEdit(false);
    }
  };

  // Admin-only: flip the setting and persist via PUT /admin/settings.
  const handleToggleAllowSelfEdit = async () => {
    if (!isAdmin) return;
    const next = !allowSelfEdit;
    setSavingSetting(true);
    try {
      const token = sessionStorage.getItem('token');
      await axios.put(
        `${API_URL}/admin/settings`,
        {
          settings: [
            {
              key: 'employees_can_set_own_hours',
              value: next ? 'true' : 'false',
              description: 'When true, employees (groomers) can edit their own working-hours schedule.',
            },
          ],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAllowSelfEdit(next);
      toast.success(next ? 'Employees can now set their own hours' : 'Self-scheduling disabled');
    } catch (e) {
      toast.error('Failed to update setting');
    } finally {
      setSavingSetting(false);
    }
  };

  const loadBusinessHours = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/business-hours`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBusinessHours(response.data);
    } catch (error) {
      console.error('Load business hours error:', error);
      toast.error('Failed to load business hours');
    }
  };

  // Generate time slots for a specific day based on business hours
  const getTimeSlotsForDay = (dayOfWeek) => {
    const businessHour = businessHours.find(h => h.day_of_week === dayOfWeek);
    
    if (!businessHour || !businessHour.is_active) {
      return [];
    }

    const slots = [];
    const [startHour, startMin] = businessHour.start_time.split(':').map(Number);
    const [endHour, endMin] = businessHour.end_time.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;

    // Generate hourly slots
    for (let minutes = startMinutes; minutes < endMinutes; minutes += 60) {
      const hour = Math.floor(minutes / 60);
      const min = minutes % 60;
      const timeStr = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
      slots.push(timeStr);
    }

    return slots;
  };

  const loadGroomerSchedule = async (groomerId) => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/business-hours/groomers/${groomerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Convert array format to object format: { dayOfWeek: { timeSlot: true/false } }
      const scheduleObj = {};
      response.data.forEach(entry => {
        if (entry.is_active) {
          const dayOfWeek = entry.day_of_week;
          if (!scheduleObj[dayOfWeek]) {
            scheduleObj[dayOfWeek] = {};
          }
          
          // Each entry is a 1-hour slot
          const startHour = parseInt(entry.start_time.split(':')[0]);
          const timeSlot = `${startHour.toString().padStart(2, '0')}:00`;
          scheduleObj[dayOfWeek][timeSlot] = true;
        }
      });
      
      setGroomerSchedule(scheduleObj);
    } catch (error) {
      console.error('Load groomer schedule error:', error);
      setGroomerSchedule({});
    }
  };

  // Self-mode users can only edit when the admin has flipped the
  // setting on. Admins always can.
  const canEdit = isAdmin || (selfMode && allowSelfEdit);

  const toggleTimeSlot = (dayOfWeek, timeSlot) => {
    if (!canEdit) return;
    console.log('Toggling:', dayOfWeek, timeSlot); // Debug log
    setGroomerSchedule(prev => {
      const newSchedule = JSON.parse(JSON.stringify(prev)); // Deep clone
      if (!newSchedule[dayOfWeek]) {
        newSchedule[dayOfWeek] = {};
      }
      const currentValue = newSchedule[dayOfWeek][timeSlot] || false;
      newSchedule[dayOfWeek][timeSlot] = !currentValue;
      console.log('New schedule:', newSchedule); // Debug log
      return newSchedule;
    });
  };

  const toggleAllDay = (dayOfWeek, enable) => {
    if (!canEdit) return;
    setGroomerSchedule(prev => {
      const newSchedule = { ...prev };
      newSchedule[dayOfWeek] = {};
      if (enable) {
        const slots = getTimeSlotsForDay(dayOfWeek);
        slots.forEach(slot => {
          newSchedule[dayOfWeek][slot] = true;
        });
      }
      return newSchedule;
    });
  };

  const handleSave = async () => {
    if (!selectedGroomer) return;
    if (!canEdit) {
      toast.error('Employee self-scheduling is currently disabled by the admin');
      return;
    }

    // Convert object format back to array format for API
    // Each selected time slot becomes a 1-hour entry
    const hoursArray = [];
    Object.keys(groomerSchedule).forEach(dayOfWeek => {
      const slots = groomerSchedule[dayOfWeek];
      const activeSlots = Object.keys(slots).filter(slot => slots[slot]);
      
      // Create an entry for each individual time slot
      activeSlots.forEach(slot => {
        const startHour = parseInt(slot.split(':')[0]);
        const endHour = startHour + 1;
        
        hoursArray.push({
          day_of_week: parseInt(dayOfWeek),
          start_time: `${startHour.toString().padStart(2, '0')}:00`,
          end_time: `${endHour.toString().padStart(2, '0')}:00`,
          is_active: true,
        });
      });
    });

    console.log('Saving schedule:', hoursArray); // Debug log

    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/business-hours/groomers/${selectedGroomer}`,
        { hours: hoursArray },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Save response:', response.data); // Debug log
      toast.success('Employee schedule updated successfully!');
      // Reload the schedule to show what was saved
      await loadGroomerSchedule(selectedGroomer);
    } catch (error) {
      console.error('Save groomer schedule error:', error);
      console.error('Error details:', error.response?.data);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to save employee schedule';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div className="flex items-center space-x-3">
          <Clock className="text-primary-600" size={24} />
          <h2 className="text-2xl font-bold">
            {selfMode ? 'My Working Hours' : 'Employee Schedules (Booking Availability)'}
          </h2>
        </div>

        {/* Admin-only toggle for the employees_can_set_own_hours setting
            (roadmap item #17 Phase 2). Hidden for groomers — they see a
            read-only banner instead. */}
        {isAdmin && !selfMode && (
          <label className="flex items-center space-x-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={allowSelfEdit}
              onChange={handleToggleAllowSelfEdit}
              disabled={savingSetting}
            />
            <span className="text-gray-700">Let employees edit their own hours</span>
          </label>
        )}
      </div>

      {/* Self-mode read-only banner when admin hasn't enabled
          self-editing. Shows the schedule but disables every control. */}
      {selfMode && !allowSelfEdit && (
        <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          <Lock size={16} className="mt-0.5 flex-shrink-0" />
          <div>
            Your administrator has not enabled self-scheduling. You can view your
            current working hours below, but only an admin can change them.
          </div>
        </div>
      )}

      <p className="text-gray-600 mb-6">
        {selfMode
          ? 'These are the hours clients can book you for. Click time slots to toggle availability, then Save.'
          : 'Set working hours for each employee. These times control when clients can book appointments.'}
        {!selfMode && groomers.length === 0 && ' Create employee accounts in the Users page first.'}
      </p>

      {groomers.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          {selfMode
            ? 'Could not find your employee profile.'
            : 'No employees found. Go to Users page to create employee accounts.'}
        </div>
      ) : (
        <div className={selfMode ? '' : 'grid grid-cols-1 lg:grid-cols-3 gap-6'}>
          {/* Groomer List — hidden in self-mode since the user can
              only ever edit themselves. */}
          {!selfMode && (
          <div>
            <h3 className="font-semibold text-gray-700 mb-3 flex items-center space-x-2">
              <Users size={20} />
              <span>Select Employee</span>
            </h3>
            <div className="space-y-2">
              {groomers.map((groomer) => (
                <button
                  key={groomer.id}
                  onClick={() => setSelectedGroomer(groomer.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                    selectedGroomer === groomer.id
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  {groomer.first_name} {groomer.last_name}
                </button>
              ))}
            </div>
          </div>
          )}

          {/* Employee Schedule */}
          <div className={selfMode ? '' : 'lg:col-span-2'}>
            {selectedGroomer ? (
              <>
                <h3 className="font-semibold text-gray-700 mb-4">
                  Schedule for {groomers.find(g => g.id === selectedGroomer)?.first_name}{' '}
                  {groomers.find(g => g.id === selectedGroomer)?.last_name}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Click time slots to mark when this employee is available. Selected slots will be available for client booking.
                </p>

                <div className="space-y-4">
                  {dayNames.map((dayName, dayOfWeek) => {
                    const daySchedule = groomerSchedule[dayOfWeek] || {};
                    const hasAnySlots = Object.values(daySchedule).some(v => v);

                    return (
                      <div key={dayOfWeek} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-semibold text-gray-800">{dayName}</span>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => toggleAllDay(dayOfWeek, true)}
                              disabled={!canEdit}
                              className="text-xs px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Select All
                            </button>
                            <button
                              onClick={() => toggleAllDay(dayOfWeek, false)}
                              disabled={!canEdit}
                              className="text-xs px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Clear All
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-6 gap-2">
                          {getTimeSlotsForDay(dayOfWeek).length > 0 ? (
                            getTimeSlotsForDay(dayOfWeek).map(slot => {
                              const isSelected = daySchedule[slot];
                              const hour = parseInt(slot.split(':')[0]);
                              const displayTime = hour > 12 ? `${hour - 12} PM` : hour === 12 ? '12 PM' : `${hour} AM`;

                              return (
                                <button
                                  key={slot}
                                  onClick={() => toggleTimeSlot(dayOfWeek, slot)}
                                  disabled={!canEdit}
                                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                    isSelected
                                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  } ${!canEdit ? 'opacity-60 cursor-not-allowed' : ''}`}
                                >
                                  {displayTime}
                                </button>
                              );
                            })
                          ) : (
                            <p className="text-sm text-gray-500 italic col-span-6">Business closed on this day</p>
                          )}
                        </div>

                        {!hasAnySlots && (
                          <p className="text-sm text-gray-500 italic mt-2">Not working this day</p>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleSave}
                    disabled={saving || !canEdit}
                    className="btn btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save size={18} />
                    <span>{saving ? 'Saving...' : 'Save Schedule'}</span>
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                Select an employee to view and edit their schedule
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GroomerSchedules;
